<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const statusConfig = {
  sent: {
    label: 'CAMPAIGN.WHATSAPP.ANALYTICS.STATUS.SENT',
    color: 'text-n-blue-11 bg-n-blue-3',
    icon: 'i-lucide-send',
  },
  delivered: {
    label: 'CAMPAIGN.WHATSAPP.ANALYTICS.STATUS.DELIVERED',
    color: 'text-n-teal-11 bg-n-teal-3',
    icon: 'i-lucide-check-check',
  },
  read: {
    label: 'CAMPAIGN.WHATSAPP.ANALYTICS.STATUS.READ',
    color: 'text-n-violet-11 bg-n-violet-3',
    icon: 'i-lucide-eye',
  },
  failed: {
    label: 'CAMPAIGN.WHATSAPP.ANALYTICS.STATUS.FAILED',
    color: 'text-n-ruby-11 bg-n-ruby-3',
    icon: 'i-lucide-alert-circle',
  },
};

const getStatusConfig = status => statusConfig[status] || statusConfig.sent;

const hasMessages = computed(() => props.messages.length > 0);

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};
</script>

<template>
  <div>
    <h4 class="mb-3 text-sm font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.RECIPIENTS') }}
      <span class="text-n-slate-10">{{ `(${messages.length})` }}</span>
    </h4>
    <div
      v-if="hasMessages"
      class="overflow-x-auto border rounded-lg border-n-weak dark:border-n-strong"
    >
      <table class="w-full text-sm min-w-[700px]">
        <thead>
          <tr
            class="border-b border-n-weak dark:border-n-strong bg-n-alpha-1 dark:bg-n-alpha-2"
          >
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.CONTACT') }}
            </th>
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.PHONE') }}
            </th>
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.STATUS') }}
            </th>
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.CLICKED') }}
            </th>
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.REPLIED') }}
            </th>
            <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.ERROR') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="msg in messages"
            :key="msg.id"
            class="border-b border-n-weak dark:border-n-strong last:border-0"
          >
            <td class="px-4 py-2.5 text-n-slate-12">
              {{ msg.contact?.name || '-' }}
            </td>
            <td class="px-4 py-2.5 text-n-slate-11 font-mono text-xs">
              {{ msg.contact?.phone_number }}
            </td>
            <td class="px-4 py-2.5">
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md"
                :class="getStatusConfig(msg.status).color"
              >
                <span
                  :class="getStatusConfig(msg.status).icon"
                  class="text-xs"
                />
                {{ t(getStatusConfig(msg.status).label) }}
              </span>
            </td>
            <td class="px-4 py-2.5 text-xs text-n-slate-11">
              <span
                v-if="msg.clicked_at"
                class="inline-flex items-center gap-1 text-n-amber-11"
              >
                <span class="i-lucide-mouse-pointer-click text-xs" />
                {{ formatDate(msg.clicked_at) }}
              </span>
              <span v-else class="text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-2.5 text-xs text-n-slate-11">
              <span
                v-if="msg.replied_at"
                class="inline-flex items-center gap-1 text-n-green-11"
              >
                <span class="i-lucide-message-circle text-xs" />
                {{ formatDate(msg.replied_at) }}
              </span>
              <span v-else class="text-n-slate-10">-</span>
            </td>
            <td
              class="px-4 py-2.5 text-xs text-n-slate-10 max-w-[200px] truncate"
            >
              {{ msg.error_message || '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="py-8 text-sm text-center text-n-slate-10">
      {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.NO_DATA') }}
    </div>
  </div>
</template>
